import { usePersistenceStatus } from '@/state/persistenceStatus';
import { authHeaders, classifyOutcome, WRITE_TIMEOUT_MS, type PersistOutcome } from '@/api/persist';
import type { Studio } from '@/domain/types';

/**
 * Persist a studio (add / edit / archive / restore) to its own table through
 * PUT /api/studios/:id (14ym9bv75kx). Studios are server-owned reference data in
 * app.studios + app.studio_agents — not the spa_state write-through surface —
 * so, like persistArtist, they get a dedicated route. Resolves to the outcome;
 * the caller reverts its optimistic store update and toasts on anything but
 * 'ok' (a 400 for an unknown manager, or no server at all).
 */
export function persistStudio(studio: Studio): Promise<PersistOutcome> {
  const { begin, settle } = usePersistenceStatus.getState();
  begin();
  const ctrl = typeof AbortController === 'undefined' ? null : new AbortController();
  const timer = ctrl ? setTimeout(() => ctrl.abort(), WRITE_TIMEOUT_MS) : null;
  return fetch(`/api/studios/${encodeURIComponent(studio.id)}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json', ...authHeaders() },
    body: JSON.stringify(studio),
    ...(ctrl ? { signal: ctrl.signal } : {}),
  })
    .then((res): PersistOutcome => {
      const outcome = classifyOutcome(res.status);
      settle(outcome, outcome === 'rejected' ? `Refused by the server (${res.status})` : undefined);
      return outcome;
    })
    .catch((): PersistOutcome => {
      settle('transient');
      return 'transient';
    })
    .finally(() => {
      if (timer) clearTimeout(timer);
    });
}
